import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { del, delMany, get, keys, set } from 'idb-keyval';

const environment = window.environments;
export interface CacheConfig {
    endpoint: string | ((data: any) => string);
    method: string;
    baseUrlEnv?: string;
    bodyDep?: string[];
    paramDep?: string[];
    configType?: string;
}

@Injectable({ providedIn: 'root' })
export class CacheService {
    public cacheConfig: CacheConfig[] = [];
    public isAvailable: boolean = false;

    // Values for fetching config to be cached
    projectId: string = 'default';
    currentTag: string = 'default';

    // Mode-aware caching
    mode: 'dev' | 'prod' = 'dev';
    branch: string = 'main';
    manifest: Map<string, string> = new Map(); // configType_configId -> version

    // Config type to endpoint reverse mapping (populated from cacheConfig)
    private endpointConfigTypeMap: Map<string, string> = new Map();

    constructor() {}

    async appInitializer(cacheConfig: CacheConfig[]) {
        try {
            this.cacheConfig = cacheConfig;
            this.isAvailable = !!window.indexedDB;

            // Build reverse mapping from endpoints to config types
            this.buildEndpointConfigTypeMap();

            // Clean up expired entries on startup
            if (this.isAvailable) {
                await this.cleanUpCache(environment?.CACHE_TTL || '5');
            }

            console.info('[Cache-Service] Initiated Caching', this.isAvailable);
        } catch (error) {
            console.error('Unable to initialize Caching');
        }
    }

    private buildEndpointConfigTypeMap() {
        for (const config of this.cacheConfig) {
            if (config.configType) {
                const endpoint = typeof config.endpoint === 'function' ? 'dynamic' : config.endpoint;
                this.endpointConfigTypeMap.set(endpoint, config.configType);
            }
        }
    }

    /**
     * Saves current version & projectId to generate cache key.
     * Detects mode switches and purges cache when mode changes.
     */
    async setProjectCred(body: any) {
        const newMode = body.devMode ? 'dev' : 'prod';

        // Mode switch detection — purge all project cache if mode changed
        if (this.projectId !== 'default' && this.mode !== newMode) {
            await this.purgeProjectCache(body.projectId || this.projectId);
        }

        this.mode = newMode;
        this.projectId = body.projectId;
        // Branch comes from URL query params, not from project response body
        const urlBranch = new URLSearchParams(window.location.search).get('branch');
        this.branch = body.branch || urlBranch || 'main';

        if (!body.devMode) {
            this.currentTag = body?.currentTag?.name;
        }
        // In devMode, currentTag is intentionally NOT stored/used for cache keys

        // Store mode for next-load comparison
        await set(`mode_${this.projectId}`, { mode: this.mode, timestamp: Date.now() });
    }

    getProjectCred() {
        return { projectId: this.projectId, currentTag: this.currentTag };
    }

    /**
     * Store manifest from /version/manifest response
     */
    setManifest(configs: Array<{ configType: string; configId: string | null; version: string }>) {
        this.manifest.clear();
        for (const c of configs) {
            const key = `${c.configType}_${c.configId || 'singleton'}`;
            this.manifest.set(key, c.version);
        }
    }

    /**
     * Fetch config manifest from backend (dev mode only).
     * Routes through the BFF proxy to avoid CORS issues.
     */
    async fetchAndSetManifest(projectId: string, branch: string = 'main'): Promise<void> {
        try {
            const response = await fetch(environment.ALPHA_UI_CONFIG_SERVICE_URL + '/version/manifest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ projectId, branch }),
            });

            if (!response.ok) {
                throw new Error(`Manifest fetch failed: ${response.status}`);
            }
            const data = await response.json();
            this.setManifest(data.configs || []);
        } catch (error) {
            console.warn('[Cache-Service] Failed to fetch manifest, caching disabled for this session', error);
        }
    }

    /**
     * Get the config version for a given config type from the manifest
     */
    getManifestVersion(configType: string, configId?: string): string | undefined {
        const key = `${configType}_${configId || 'singleton'}`;
        return this.manifest.get(key);
    }

    /**
     * Generates a unique Cache key for each endpoint being cached.
     * Mode-aware: dev keys include branch + config version, prod keys include currentTag.
     */
    generateCacheKey(endpoint: string, method: string, requestBodyKey?: string, url?: string): string {
        if (this.mode === 'dev') {
            const configType = this.resolveConfigTypeForEndpoint(endpoint);
            const configVersion = configType ? this.manifest.get(`${configType}_singleton`) || 'unknown' : 'unknown';
            return `${this.projectId}_dev_${this.branch}_[${configType || 'unknown'}]_${method}_${endpoint}_${requestBodyKey || 'default'}_${configVersion}`;
        }
        // Prod mode — unchanged from original
        return `${this.projectId}_prod_${this.currentTag}_${method}_${endpoint}_${requestBodyKey || 'default'}_${url || 'default'}`;
    }

    /**
     * Resolve the config type for a given endpoint URL
     */
    private resolveConfigTypeForEndpoint(endpoint: string): string | null {
        for (const [ep, configType] of this.endpointConfigTypeMap) {
            if (ep !== 'dynamic' && endpoint.includes(ep)) {
                return configType;
            }
        }
        // Check dynamic endpoints from cacheConfig
        for (const config of this.cacheConfig) {
            if (config.configType && typeof config.endpoint === 'function') {
                const resolved = config.endpoint(this.projectId);
                if (endpoint.includes(resolved)) {
                    return config.configType;
                }
            }
        }
        return null;
    }

    /**
     * Generates requestBody key for each endpoint being cached
     */
    generateRequestBodyKey(data: { body: any; params: HttpParams }, endpointData: CacheConfig): string {
        let linkedBodyData: string[] = [];
        let linkedParamData: string[] = [];

        // Process bodyDep
        if (endpointData.bodyDep?.length) {
            linkedBodyData = endpointData.bodyDep
                .map((key) => {
                    if (data.body && key in data.body) {
                        return data.body[key] as string;
                    }
                    return null;
                })
                .filter((value): value is string => value !== null);
        }

        // Process paramDep
        if (endpointData.paramDep?.length && data.params?.get) {
            linkedParamData = endpointData.paramDep
                .map((key) => {
                    const paramValue = data.params.get(key);
                    return paramValue ? paramValue : null;
                })
                .filter((value): value is string => value !== null);
        }

        return [...linkedBodyData, ...linkedParamData].join('_');
    }

    // Find Endpoint using constructed url
    findEndpoint(url: string, method: string, body: any) {
        return this.cacheConfig.find((obj) => {
            let constructedEndpoint: string | null = null;

            if (typeof obj.endpoint === 'function') {
                constructedEndpoint = obj.endpoint(this.projectId);
            } else {
                constructedEndpoint = obj.endpoint;
            }

            // Use includes for dynamic (function) endpoints to handle varying query params,
            // endsWith for static string endpoints for exact matching
            const urlMatches =
                constructedEndpoint &&
                (typeof obj.endpoint === 'function'
                    ? url.includes(constructedEndpoint)
                    : url.endsWith(constructedEndpoint));

            if (obj?.baseUrlEnv && obj.baseUrlEnv.length) {
                const matchesBaseUrlEnv: boolean =
                    typeof body?.body?.url === 'string'
                        ? body.body.url.startsWith(environment[obj?.baseUrlEnv])
                        : body?.url?.value?.__alpha_env__ == obj?.baseUrlEnv;

                return urlMatches && obj.method.toLowerCase() === method?.toLowerCase() && matchesBaseUrlEnv;
            } else {
                return urlMatches && obj.method.toLowerCase() === method?.toLowerCase();
            }
        });
    }

    // Get data from the cache
    async getConfig(key: string, ttlDays: string): Promise<any | null> {
        try {
            const cachedEntry = await get(key);
            if (cachedEntry) {
                const { timestamp, data } = cachedEntry;
                const now = Date.now();

                let days = Number(ttlDays);
                const ttl = (!isNaN(days) ? days : 1) * 24 * 60 * 60 * 1000;

                if (now - timestamp > ttl) {
                    await del(key);
                    return null;
                }

                set(key, { ...cachedEntry, timestamp: Date.now() });
                return data;
            }
            return null;
        } catch (error) {
            console.log(`Failed to get Cache for key: ${key}`, error);
        }
    }

    // Set data in the cache
    async setConfig(cacheEntry: { key: string; data: any }): Promise<void> {
        try {
            const { key, data } = cacheEntry;
            await set(key, {
                data,
                projectId: this.projectId,
                currentTag: this.currentTag,
                mode: this.mode,
                branch: this.branch,
                timestamp: Date.now(),
            });
        } catch (error) {
            console.log(`Unable to set Cache for key: ${cacheEntry.key}`, error);
        }
    }

    // Invalidate cache entries by project ID or version (prod mode — tag-based)
    async invalidateCache(): Promise<void> {
        try {
            const allKeys = (await keys()) as string[];

            // get the latest project metadata saved
            const currentProject = await get(`project_${this.projectId}`);

            if (currentProject) {
                const { projectId, currentTag } = currentProject;

                if (currentTag !== this.currentTag) {
                    const currentProjectKeys = allKeys.filter((key: string) =>
                        key.startsWith(`${projectId}_prod_${currentTag}_`),
                    );
                    await delMany(currentProjectKeys);
                }
            }
        } catch (error) {
            console.log(`Unable to invalidate Cache`, error);
        }
    }

    /**
     * Manifest-based selective invalidation (dev mode).
     * Compares new manifest versions against stored manifest.
     * Only deletes IndexedDB entries for config types whose versions changed.
     */
    async invalidateByManifest(): Promise<void> {
        try {
            const stored = await get(`manifest_${this.projectId}_${this.branch}`);
            if (!stored?.configs) {
                // First visit — nothing to invalidate, just save current manifest
                await this.saveManifestToIndexedDB();
                return;
            }

            const oldManifest = new Map<string, string>();
            for (const c of stored.configs) {
                oldManifest.set(`${c.configType}_${c.configId || 'singleton'}`, c.version);
            }

            // Find config types whose versions changed
            const changedTypes = new Set<string>();
            for (const [key, version] of this.manifest) {
                if (oldManifest.get(key) !== version) {
                    const separatorIdx = key.lastIndexOf('_');
                    changedTypes.add(key.substring(0, separatorIdx)); // extract configType
                }
            }

            // Save new manifest AFTER reading old one for comparison
            await this.saveManifestToIndexedDB();

            if (changedTypes.size === 0) return;

            // Delete IndexedDB entries for changed config types
            const allKeys = (await keys()) as string[];
            const keysToDelete = allKeys.filter((key: string) => {
                if (!key.startsWith(`${this.projectId}_dev_`)) return false;
                return [...changedTypes].some((ct) => key.includes(`[${ct}]`));
            });
            if (keysToDelete.length) {
                await delMany(keysToDelete);
                console.info(
                    `[Cache-Service] Invalidated ${keysToDelete.length} entries for changed configs: ${[...changedTypes].join(', ')}`,
                );
            }
        } catch (error) {
            console.log('[Cache-Service] Unable to invalidate by manifest', error);
        }
    }

    /**
     * Persist current manifest to IndexedDB for comparison on next load.
     */
    private async saveManifestToIndexedDB(): Promise<void> {
        const configs = Array.from(this.manifest.entries()).map(([key, version]) => {
            const separatorIdx = key.lastIndexOf('_');
            const configType = key.substring(0, separatorIdx);
            const configId = key.substring(separatorIdx + 1);
            return { configType, configId: configId === 'singleton' ? null : configId, version };
        });
        await set(`manifest_${this.projectId}_${this.branch}`, {
            configs,
            timestamp: Date.now(),
        });
    }

    /**
     * Purge all cache entries for a project (used on mode switches)
     */
    async purgeProjectCache(projectId: string): Promise<void> {
        try {
            const allKeys = (await keys()) as string[];
            const projectKeys = allKeys.filter((key: string) => key.toString().startsWith(projectId));
            if (projectKeys.length) {
                await delMany(projectKeys);
                console.info(
                    `[Cache-Service] Mode switch detected — purged ${projectKeys.length} cache entries for project ${projectId}`,
                );
            }
        } catch (error) {
            console.log('[Cache-Service] Unable to purge project cache', error);
        }
    }

    async cleanUpCache(ttlDays: string) {
        try {
            const allKeys = (await keys()) as string[];
            const keysToDelete: string[] = [];
            const now = Date.now();
            let days = Number(ttlDays);
            const ttl = (!isNaN(days) ? days : 1) * 24 * 60 * 60 * 1000;

            for (const key of allKeys) {
                const data = await get(key);
                if (data?.timestamp && now - data.timestamp > ttl) {
                    keysToDelete.push(key as string);
                }
            }

            if (keysToDelete.length) {
                await delMany(keysToDelete);
                console.info(`[Cache-Service] Cleaned up ${keysToDelete.length} expired cache entries`);
            }
        } catch (error) {
            console.log('unable to clean up cache');
        }
    }
}
